import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment as ENV } from 'src/environments/environment';
import { LoggerConfig } from '../config/logger.config';
import { ErrorCheckResult } from '../interface/logger.interface';

@Injectable({ providedIn: 'root' })
export class LoggerService {
  /** Array of timestamps when errors were logged (for rate limiting) */
  private _errorTimestamps: number[] = [];

  /** Map of error fingerprints to their last logged timestamp (for deduplication) */
  private _errorFingerprintMap: Map<string, number> = new Map();

  /** Fingerprint of the last error encountered */
  private _lastErrorFingerprint: string = '';

  /** Count of consecutive errors with the same fingerprint */
  private _consecutiveErrorCount: number = 0;

  /** Flag to track if error loop notification has been shown */
  private _errorLoopDetected: boolean = false;

  constructor(private http: HttpClient) {}

  /**
   * Determines whether an error should be logged based on rate limiting,
   * deduplication, and error loop detection rules.
   */
  shouldLogError(message: string, stack: string): ErrorCheckResult {
    const fingerprint = this._generateErrorFingerprint(message, stack);

    // Check for error loop (consecutive similar errors)
    const isErrorLoop = this._trackConsecutiveError(fingerprint);
    if (isErrorLoop && !this._errorLoopDetected) {
      this._errorLoopDetected = true;
      return {
        shouldLog: false,
        isErrorLoop: true,
        isRateLimited: false,
        reason: 'Error loop detected: same error repeated 5+ times consecutively',
      };
    } else if (isErrorLoop) {
      // Error loop already detected, silently suppress
      return {
        shouldLog: false,
        isErrorLoop: true,
        isRateLimited: false,
        reason: 'Error loop already in progress',
      };
    }

    // Check rate limiting
    if (this._isRateLimitExceeded()) {
      return {
        shouldLog: false,
        isErrorLoop: false,
        isRateLimited: true,
        reason: `Rate limit exceeded: max ${LoggerConfig.defaultErrorLoggingConfig.maxErrorsPerWindow} errors per ${LoggerConfig.defaultErrorLoggingConfig.timeWindowMs}ms`,
      };
    }

    // Check deduplication
    if (this._isErrorDuplicate(fingerprint)) {
      return {
        shouldLog: false,
        isErrorLoop: false,
        isRateLimited: false,
        reason: `Duplicate error: same error logged within last ${LoggerConfig.defaultErrorLoggingConfig.deduplicationWindowMs}ms`,
      };
    }

    // All checks passed, allow logging
    return {
      shouldLog: true,
      isErrorLoop: false,
      isRateLimited: false,
    };
  }

  /**
   * Logs an error to the backend API.
   * Should only be called after checking shouldLogError().
   */
  logError(message: string, stack?: string, data?: unknown): void {
    const now = Date.now();
    const stackTrace = stack ?? '';
    const fingerprint = this._generateErrorFingerprint(message, stackTrace);

    // Update tracking data
    this._errorTimestamps.push(now);
    this._errorFingerprintMap.set(fingerprint, now);

    const payload = {
      message,
      logLevel: 'ERROR',
      callStack: stackTrace,
      data: data || {},
    };

    this.http.post(`${ENV.backendBaseUrl}/api/log`, payload).subscribe({
      next: () => {},
    });
  }

  /**
   * Generates a unique fingerprint for an error based on its message and stack trace.
   * This is used for deduplication and consecutive error tracking.
   */
  private _generateErrorFingerprint(message: string, stack: string): string {
    // Use first 200 characters of stack trace to create a unique identifier
    const stackSubstring = stack.substring(0, 200);
    return `${message}|${stackSubstring}`;
  }

  /**
   * Checks if rate limit has been exceeded by removing old timestamps
   * and comparing current error count against the configured maximum.
   */
  private _isRateLimitExceeded(): boolean {
    const now = Date.now();
    const cutoffTime = now - LoggerConfig.defaultErrorLoggingConfig.timeWindowMs;

    // Remove timestamps outside the current time window
    this._errorTimestamps = this._errorTimestamps.filter((timestamp) => timestamp > cutoffTime);

    return this._errorTimestamps.length >= LoggerConfig.defaultErrorLoggingConfig.maxErrorsPerWindow;
  }

  /**
   * Checks if the same error was logged recently within the deduplication window.
   */
  private _isErrorDuplicate(fingerprint: string): boolean {
    const lastLogged = this._errorFingerprintMap.get(fingerprint);
    if (!lastLogged) {
      return false;
    }

    const now = Date.now();
    const timeSinceLastLog = now - lastLogged;

    return timeSinceLastLog < LoggerConfig.defaultErrorLoggingConfig.deduplicationWindowMs;
  }

  /**
   * Tracks consecutive similar errors to detect error loops.
   * Returns true if error loop threshold is reached.
   */
  private _trackConsecutiveError(fingerprint: string): boolean {
    if (fingerprint === this._lastErrorFingerprint) {
      this._consecutiveErrorCount++;
    } else {
      this._consecutiveErrorCount = 1;
      this._lastErrorFingerprint = fingerprint;
    }

    return this._consecutiveErrorCount >= LoggerConfig.defaultErrorLoggingConfig.consecutiveErrorThreshold;
  }
}
